import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { ProcureAction, ProcureGetter } from '@/store/procure/procure.module';
import { ProcureEndDateSchema } from '@/store/procure/procure.validator';
import { Procure, ProjectEndDate } from '@/store/procure/procure.model';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProjectProcureEndDateFormComponent',
  components: {
    Datepicker,
    BaseModalComponent
  },
  props: {
    projectId: {
      type: Number,
    },
    endDateId: {
      type: Number,
    }
  },
  emits: [
    'hidden'
  ],
  setup: (props, context) => {
    const swal = useSweetAlert();
    const store = useStore();
    const isEditMode = computed(() => props.endDateId != null);
    const canDelete = computed(() => (procure.value) ? procure.value?.endDates.length > 1 : false);
    const title = computed(() => isEditMode.value ? 'แก้ไขวันที่สิ้นสุดโครงการ' : 'เพิ่มวันที่สิ้นสุดโครงการ');
    const procure = computed<Procure | undefined>(() => store.getters[ProcureGetter.GET]);
    const enddate = computed<ProjectEndDate | undefined>(() => procure.value?.endDates.find(m => m.id === props.endDateId));
    const enddateForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      endDate?: Date | null;
      remark?: string | null;
    }
    const formData = reactive<Form>({
      endDate: null,
      remark: ''
    });
    const validator = computed(() => ProcureEndDateSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      endDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'endDate')),
      remark: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'remark'))
    });
    const isValid = computed(() => !errors.endDate && !errors.remark);
    const isLoading = computed<boolean>(() => store.getters[ProcureGetter.STATUS] === 'init' || store.getters[ProcureGetter.STATUS] === 'loading');
    const submited = ref(false);


    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.endDate = enddate.value?.endDate;
          formData.remark = enddate.value?.remark;
        }
        resolve();
      })
      .then(() => enddateForm.value?.show());
    };

    // hide modal
    const hide = () => {
      enddateForm.value?.hide();
    };


    const handleHidden = () => {
      context.emit('hidden');
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.endDate = null;
        formData.remark = '';
        resolve();
      }).then(() => unsubmit());
    };


    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(ProcureAction.EDIT_ENDDATE, {
            projectId: props.projectId,
            id: props.endDateId,
            endDate: formData.endDate,
            remark: formData.remark,
          }).then(() => hide());
        } else {
          store.dispatch(ProcureAction.ADD_ENDDATE, {
            projectId: props.projectId,
            endDate: formData.endDate,
            remark: formData.remark,
          })
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(ProcureAction.REMOVE_ENDDATE, {
              projectId: props.projectId,
              id: props.endDateId
            }).then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      canDelete,
      enddateForm,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      deleteClick,
      handleHidden,
    };
  },
});
